import { createContext, useState, useContext, useEffect, useCallback } from 'react';
import apiClient from './apiClient';

const AuthContext = createContext();

export const SESSION_CHECK_INTERVAL_MS = 4 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);

  const verifySession = useCallback(async () => {
    try {
      const response = await apiClient.get('/auth.php?action=verify');
      if (response.data.success) {
        setIsAuthenticated(true);
        setUser({
          email: response.data.user.email,
          role: response.data.user.role,
          id: response.data.user.id,
          nome: response.data.user.nome,
          cognome: response.data.user.cognome,
          is_advanced: !!response.data.user.is_advanced,
          restricted_view: !!response.data.user.restricted_view,
        });
      } else {
        setIsAuthenticated(false);
        setUser(null);
        // La sessione non è più valida lato server (es. account cancellato): distruggiamo
        // esplicitamente il cookie di sessione PHP, non solo lo stato React — altrimenti
        // resterebbe utilizzabile per chiamate dirette alle altre API finché non scade da sé.
        try {
          await apiClient.post('/auth.php?action=logout');
        } catch (logoutError) {
          console.error('Errore durante la chiusura della sessione non valida:', logoutError);
        }
      }
    } catch (error) {
      console.error('Errore durante la verifica della sessione:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    verifySession();
    const intervalId = setInterval(verifySession, SESSION_CHECK_INTERVAL_MS);
    // I browser possono rallentare i setInterval nelle schede in background: quando
    // l'utente torna sulla scheda, riverifichiamo subito invece di aspettare fino a
    // SESSION_CHECK_INTERVAL_MS.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        verifySession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [verifySession]);

  const login = async (email, role, id) => {
    try {
      const response = await apiClient.post('/users.php?action=getUserDetails', {
        userIds: [id],
      });
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const userDetails = response.data[0];
        const { nome, cognome, is_advanced, restricted_view } = userDetails;
        setIsAuthenticated(true);
        setUser({ email, role, id, nome, cognome, is_advanced: !!is_advanced, restricted_view: !!restricted_view });
      } else {
        console.error('Errore nel recupero dei dati utente: Nessun dato trovato');
        setIsAuthenticated(true);
        setUser({ email, role, id, nome: '', cognome: '', is_advanced: false, restricted_view: false });
      }
    } catch (error) {
      console.error('Errore durante il recupero dei dati utente:', error);
      setIsAuthenticated(true);
      setUser({ email, role, id, nome: '', cognome: '', is_advanced: false, restricted_view: false });
    }
  };

  const logout = async () => {
    try {
      await apiClient.post('/auth.php?action=logout');
    } catch (error) {
      console.error('Errore durante il logout:', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
    }
  };

  const getUserId = () => user?.id;

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, login, logout, getUserId }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);